import React, { useEffect, useState } from "react";
import {
  Envelope,
  Plus,
  Trash,
  PencilSimple,
  Check,
  X,
  Plugs,
  ArrowsClockwise,
  CircleNotch,
  Warning,
  ShieldCheck,
  ListChecks,
  ClockCounterClockwise,
  Play,
  DotsSixVertical,
} from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";

const API = `${window.location.origin}/api`;

const FIELD_LABELS = {
  sender: "Nadawca",
  domain: "Domena",
  subject: "Temat",
  body: "Treść",
  has_attachment: "Załącznik",
};
const OP_LABELS = {
  contains: "zawiera",
  equals: "równa się",
  starts_with: "zaczyna się od",
  ends_with: "kończy się na",
  regex: "regex",
  is_true: "istnieje",
  is_false: "brak",
};
const ACTION_LABELS = {
  move: "Przenieś do",
  copy: "Kopiuj do",
  mark_read: "Oznacz jako przeczytane",
  mark_important: "Oznacz jako ważne",
};

function emptyMailbox() {
  return {
    name: "",
    email: "",
    imap_host: "imap.titan.email",
    imap_port: 993,
    imap_ssl: true,
    username: "",
    password: "",
    active: true,
    sync_interval_minutes: 15,
    search_mode: "unseen",
    search_limit: 20,
  };
}

function emptyRule(mailboxId) {
  return {
    mailbox_id: mailboxId,
    name: "",
    priority: 100,
    active: true,
    match: "all",
    conditions: [{ field: "subject", op: "contains", value: "" }],
    actions: [{ type: "move", folder: "" }],
  };
}

export default function PocztaTab() {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);

  const [mbxForm, setMbxForm] = useState(null); // { editing_id?, ...fields }
  const [ruleForm, setRuleForm] = useState(null); // { editing_id?, ... }
  const [busy, setBusy] = useState({}); // { mbxId: "test"|"sync"|"dryrun" }
  const [flash, setFlash] = useState(null); // { kind, msg }
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dryRun, setDryRun] = useState(null); // { rule_id?, name?, result }
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragFromIdx, setDragFromIdx] = useState(null);
  const [folders, setFolders] = useState([]);   // available IMAP folders for autocomplete
  const [foldersError, setFoldersError] = useState(null);

  const selected = mailboxes.find((m) => m.id === selectedId) || null;

  const loadMailboxes = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/imap/mailboxes`);
      setMailboxes(data || []);
      if ((data || []).length && !selectedId) setSelectedId(data[0].id);
    } catch (e) {
      console.error("mailboxes load", e);
    } finally {
      setLoading(false);
    }
  };

  const loadRules = async (mid) => {
    if (!mid) return;
    setRulesLoading(true);
    try {
      const { data } = await axios.get(`${API}/imap/mailboxes/${mid}/rules`);
      setRules(data || []);
    } catch (e) {
      console.error("rules load", e);
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  };

  useEffect(() => { loadMailboxes(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selectedId) loadRules(selectedId); else setRules([]); }, [selectedId]);

  // Load available IMAP folders when a mailbox is selected — used by folder
  // autocomplete inside the rule modal. Silent failure (folders are optional).
  useEffect(() => {
    let cancelled = false;
    if (!selectedId) { setFolders([]); setFoldersError(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/imap/mailboxes/${selectedId}/folders`);
        if (!cancelled) { setFolders(data?.folders || []); setFoldersError(null); }
      } catch (e) {
        if (!cancelled) {
          setFolders([]);
          setFoldersError(e?.response?.data?.detail || "Nie udało się pobrać listy folderów");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const notify = (kind, msg) => {
    setFlash({ kind, msg });
    setTimeout(() => setFlash(null), 4500);
  };

  // ---------- Mailbox actions ----------
  const openNewMailbox = () => setMbxForm({ ...emptyMailbox() });
  const openEditMailbox = (m) => setMbxForm({
    editing_id: m.id,
    name: m.name || "",
    email: m.email || "",
    imap_host: m.imap_host || "",
    imap_port: m.imap_port || 993,
    imap_ssl: !!m.imap_ssl,
    username: m.username || "",
    password: "", // never prefilled
    active: !!m.active,
    sync_interval_minutes: m.sync_interval_minutes ?? 15,
    search_mode: m.search_mode || "unseen",
    search_limit: m.search_limit ?? 20,
  });

  const saveMailbox = async () => {
    if (!mbxForm) return;
    const isEdit = !!mbxForm.editing_id;
    const payload = { ...mbxForm };
    delete payload.editing_id;
    if (isEdit && !payload.password) delete payload.password; // don't overwrite
    try {
      if (isEdit) {
        await axios.put(`${API}/imap/mailboxes/${mbxForm.editing_id}`, payload);
      } else {
        await axios.post(`${API}/imap/mailboxes`, payload);
      }
      setMbxForm(null);
      await loadMailboxes();
      notify("ok", isEdit ? "Zaktualizowano skrzynkę" : "Skrzynka dodana");
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd zapisu skrzynki");
    }
  };

  const deleteMailbox = async (m) => {
    if (!window.confirm(`Usunąć skrzynkę „${m.name}" wraz z regułami?`)) return;
    try {
      await axios.delete(`${API}/imap/mailboxes/${m.id}`);
      if (selectedId === m.id) setSelectedId(null);
      await loadMailboxes();
      notify("ok", "Skrzynka usunięta");
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd usuwania");
    }
  };

  const testMailbox = async (m) => {
    setBusy((b) => ({ ...b, [m.id]: "test" }));
    try {
      const { data } = await axios.post(`${API}/imap/mailboxes/${m.id}/test`);
      notify("ok", data?.message || "Połączenie OK");
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd połączenia");
    } finally {
      setBusy((b) => ({ ...b, [m.id]: null }));
    }
  };

  const syncMailbox = async (m) => {
    setBusy((b) => ({ ...b, [m.id]: "sync" }));
    try {
      const { data } = await axios.post(`${API}/imap/mailboxes/${m.id}/sync`);
      if (data.ok) {
        notify("ok", `Sync OK — sprawdzono ${data.processed}, dopasowań ${data.matched}, akcji ${data.actions_applied}`);
      } else {
        notify("err", `Sync błąd: ${data.error || "nieznany"}`);
      }
      await loadMailboxes();
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd synchronizacji");
    } finally {
      setBusy((b) => ({ ...b, [m.id]: null }));
    }
  };

  const openHistory = async () => {
    if (!selected) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/imap/mailboxes/${selected.id}/history?limit=30`);
      setHistory(data || []);
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd pobierania historii");
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const runDryRun = async (opts = {}) => {
    if (!selected) return;
    const { rule_id, name } = opts;
    setBusy((b) => ({ ...b, [selected.id]: "dryrun" }));
    try {
      const q = rule_id ? `?rule_id=${encodeURIComponent(rule_id)}` : "";
      const { data } = await axios.post(`${API}/imap/mailboxes/${selected.id}/dry-run${q}`);
      setDryRun({ rule_id: rule_id || null, name: name || null, result: data });
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd dry-run");
    } finally {
      setBusy((b) => ({ ...b, [selected.id]: null }));
    }
  };

  // ---------- Drag & drop reorder ----------
  const onDragStart = (idx) => setDragFromIdx(idx);
  const onDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const onDragEnd = () => { setDragFromIdx(null); setDragOverIdx(null); };
  const onDrop = async (idx) => {
    if (dragFromIdx == null || dragFromIdx === idx || !selected) { onDragEnd(); return; }
    const next = [...rules];
    const [moved] = next.splice(dragFromIdx, 1);
    next.splice(idx, 0, moved);
    // Optimistic update
    setRules(next.map((r, i) => ({ ...r, priority: (i + 1) * 10 })));
    onDragEnd();
    try {
      await axios.put(`${API}/imap/mailboxes/${selected.id}/rules/reorder`, {
        rule_ids: next.map((r) => r.id),
      });
      await loadRules(selected.id);
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd zmiany kolejności");
      await loadRules(selected.id); // revert
    }
  };

  // ---------- Rule actions ----------
  const openNewRule = () => selected && setRuleForm(emptyRule(selected.id));
  const openEditRule = (r) => setRuleForm({
    editing_id: r.id,
    mailbox_id: r.mailbox_id,
    name: r.name,
    priority: r.priority,
    active: r.active,
    match: r.match,
    conditions: r.conditions?.length ? r.conditions.map((c) => ({ ...c })) : [{ field: "subject", op: "contains", value: "" }],
    actions: r.actions?.length ? r.actions.map((a) => ({ ...a })) : [{ type: "move", folder: "" }],
  });

  const saveRule = async () => {
    if (!ruleForm || !selected) return;
    const { editing_id, mailbox_id, ...payload } = ruleForm;
    // Clean conditions: strip value where op is is_true/is_false
    payload.conditions = (payload.conditions || []).map((c) => {
      const clean = { field: c.field, op: c.op, value: c.value || "" };
      if (c.op === "is_true" || c.op === "is_false") clean.value = "";
      return clean;
    });
    payload.actions = (payload.actions || []).map((a) => ({
      type: a.type,
      folder: (a.type === "move" || a.type === "copy") ? (a.folder || "") : null,
    }));
    try {
      if (editing_id) {
        await axios.put(`${API}/imap/rules/${editing_id}`, payload);
      } else {
        await axios.post(`${API}/imap/mailboxes/${mailbox_id}/rules`, payload);
      }
      setRuleForm(null);
      await loadRules(selected.id);
      notify("ok", editing_id ? "Reguła zaktualizowana" : "Reguła dodana");
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd zapisu reguły");
    }
  };

  const deleteRule = async (r) => {
    if (!window.confirm(`Usunąć regułę „${r.name}"?`)) return;
    try {
      await axios.delete(`${API}/imap/rules/${r.id}`);
      await loadRules(selected.id);
      notify("ok", "Reguła usunięta");
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd usuwania reguły");
    }
  };

  const toggleRuleActive = async (r) => {
    try {
      await axios.put(`${API}/imap/rules/${r.id}`, { active: !r.active });
      await loadRules(selected.id);
    } catch (e) {
      notify("err", e?.response?.data?.detail || "Błąd");
    }
  };

  return (
    <div className="space-y-6" data-testid="poczta-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#0B1E3F] text-white flex items-center justify-center">
            <Envelope className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold leading-tight">Poczta</h2>
            <p className="text-xs text-muted-foreground">Skrzynki IMAP i reguły automatycznego sortowania</p>
          </div>
        </div>
        <button
          onClick={openNewMailbox}
          className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1E40AF] text-white text-sm px-4 py-2 rounded-md"
          data-testid="mailbox-new-btn"
        >
          <Plus className="w-4 h-4" /> Nowa skrzynka
        </button>
      </div>

      {flash && (
        <div
          className={`text-sm px-4 py-2 rounded-md border ${flash.kind === "ok" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}
          data-testid="poczta-flash"
        >
          {typeof flash.msg === "string" ? flash.msg : JSON.stringify(flash.msg)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Left column: mailboxes list */}
        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Skrzynki ({mailboxes.length})
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
              <CircleNotch className="w-4 h-4 animate-spin" /> Ładowanie…
            </div>
          ) : mailboxes.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">Brak skrzynek. Dodaj pierwszą powyżej.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {mailboxes.map((m) => {
                const isSel = m.id === selectedId;
                const st = m.last_sync_status || "";
                const stErr = st.startsWith("error");
                return (
                  <li
                    key={m.id}
                    onClick={() => setSelectedId(m.id)}
                    className={`p-3 cursor-pointer hover:bg-slate-50 ${isSel ? "bg-slate-100" : ""}`}
                    data-testid={`mailbox-item-${m.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{m.name}</div>
                        <div className="text-xs text-slate-500 truncate">{m.email}</div>
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
                          {m.active ? <ShieldCheck className="w-3 h-3 text-green-600" /> : <Warning className="w-3 h-3 text-slate-400" />}
                          <span>{m.sync_interval_minutes ? `co ${m.sync_interval_minutes} min` : "ręcznie"}</span>
                          {st && (
                            <span className={`ml-1 truncate ${stErr ? "text-red-600" : "text-green-700"}`} title={st}>· {stErr ? "błąd" : "ok"}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          className="p-1 hover:bg-slate-200 rounded"
                          onClick={(e) => { e.stopPropagation(); openEditMailbox(m); }}
                          title="Edytuj"
                          data-testid={`mailbox-edit-${m.id}`}
                        >
                          <PencilSimple className="w-4 h-4 text-slate-600" />
                        </button>
                        <button
                          className="p-1 hover:bg-red-100 rounded"
                          onClick={(e) => { e.stopPropagation(); deleteMailbox(m); }}
                          title="Usuń"
                          data-testid={`mailbox-delete-${m.id}`}
                        >
                          <Trash className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Right column: rules for selected mailbox */}
        <div className="bg-white rounded-md border border-slate-200">
          {!selected ? (
            <div className="p-8 text-sm text-slate-500">Wybierz skrzynkę po lewej.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm">{selected.name}</div>
                  <div className="text-xs text-slate-500">
                    {selected.imap_host}:{selected.imap_port} · {selected.imap_ssl ? "SSL" : "no-SSL"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => testMailbox(selected)}
                    className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-sm px-3 py-1.5 rounded-md"
                    disabled={busy[selected.id] === "test"}
                    data-testid="mailbox-test-btn"
                  >
                    {busy[selected.id] === "test" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Plugs className="w-4 h-4" />}
                    Testuj
                  </button>
                  <button
                    onClick={() => syncMailbox(selected)}
                    className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-sm px-3 py-1.5 rounded-md"
                    disabled={busy[selected.id] === "sync"}
                    data-testid="mailbox-sync-btn"
                  >
                    {busy[selected.id] === "sync" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ArrowsClockwise className="w-4 h-4" />}
                    Synchronizuj
                  </button>
                  <button
                    onClick={() => runDryRun({})}
                    className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-sm px-3 py-1.5 rounded-md"
                    disabled={busy[selected.id] === "dryrun"}
                    title="Sprawdź, które ostatnie 50 wiadomości pasują do reguł — bez wykonywania akcji"
                    data-testid="mailbox-dryrun-btn"
                  >
                    {busy[selected.id] === "dryrun" ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    Dry-run
                  </button>
                  <button
                    onClick={openHistory}
                    className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-sm px-3 py-1.5 rounded-md"
                    data-testid="mailbox-history-btn"
                  >
                    <ClockCounterClockwise className="w-4 h-4" />
                    Historia
                  </button>
                  <button
                    onClick={openNewRule}
                    className="inline-flex items-center gap-1 bg-[#2563EB] hover:bg-[#1E40AF] text-white text-sm px-3 py-1.5 rounded-md"
                    data-testid="rule-new-btn"
                  >
                    <Plus className="w-4 h-4" /> Nowa reguła
                  </button>
                </div>
              </div>

              <div className="p-4">
                {rulesLoading ? (
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <CircleNotch className="w-4 h-4 animate-spin" /> Ładowanie reguł…
                  </div>
                ) : rules.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">
                    <ListChecks className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                    Brak reguł. Dodaj pierwszą regułę sortowania.
                  </div>
                ) : (
                  <>
                    {rules.length > 1 && (
                      <div className="text-[11px] text-slate-400 mb-2 flex items-center gap-1">
                        <DotsSixVertical className="w-3 h-3" />
                        Przeciągnij regułę za uchwyt, aby zmienić priorytet
                      </div>
                    )}
                    <ul className="divide-y divide-slate-100" data-testid="rules-list">
                      {rules.map((r, idx) => (
                        <li
                          key={r.id}
                          onDragOver={(e) => onDragOver(e, idx)}
                          onDrop={() => onDrop(idx)}
                          onDragEnd={onDragEnd}
                          className={`py-3 flex items-start justify-between gap-3 ${dragOverIdx === idx && dragFromIdx !== null && dragFromIdx !== idx ? "bg-blue-50" : ""} ${dragFromIdx === idx ? "opacity-40" : ""}`}
                          data-testid={`rule-item-${r.id}`}
                        >
                          <div
                            draggable
                            onDragStart={() => onDragStart(idx)}
                            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 pt-1 select-none"
                            title="Przeciągnij, aby zmienić priorytet"
                            data-testid={`rule-drag-${r.id}`}
                          >
                            <DotsSixVertical className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono bg-slate-100 rounded px-1.5 py-0.5">#{r.priority}</span>
                              <span className="font-semibold text-sm">{r.name}</span>
                              <span className={`text-[11px] px-1.5 py-0.5 rounded ${r.active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}`}>
                                {r.active ? "aktywna" : "wyłączona"}
                              </span>
                              <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 uppercase">
                                {r.match === "all" ? "AND (wszystkie)" : "OR (dowolny)"}
                              </span>
                            </div>
                          <div className="mt-1 text-xs text-slate-600 space-y-0.5">
                            {(r.conditions || []).map((c, i) => (
                              <div key={i}>
                                • {FIELD_LABELS[c.field] || c.field} <span className="text-slate-400">{OP_LABELS[c.op] || c.op}</span> {c.value ? <span className="font-mono">„{c.value}"</span> : null}
                              </div>
                            ))}
                          </div>
                          <div className="mt-1 text-xs text-slate-700">
                            {(r.actions || []).map((a, i) => (
                              <span key={i} className="inline-block bg-slate-100 rounded px-1.5 py-0.5 mr-1 mb-1">
                                {ACTION_LABELS[a.type] || a.type}{a.folder ? `: ${a.folder}` : ""}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            onClick={() => toggleRuleActive(r)}
                            className="p-1 hover:bg-slate-100 rounded"
                            title={r.active ? "Wyłącz" : "Włącz"}
                            data-testid={`rule-toggle-${r.id}`}
                          >
                            {r.active ? <Check className="w-4 h-4 text-green-600" /> : <X className="w-4 h-4 text-slate-400" />}
                          </button>
                          <button
                            onClick={() => openEditRule(r)}
                            className="p-1 hover:bg-slate-100 rounded"
                            title="Edytuj"
                            data-testid={`rule-edit-${r.id}`}
                          >
                            <PencilSimple className="w-4 h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => deleteRule(r)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Usuń"
                            data-testid={`rule-delete-${r.id}`}
                          >
                            <Trash className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </li>
                    ))}
                    </ul>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {mbxForm && (
        <MailboxModal
          form={mbxForm}
          setForm={setMbxForm}
          onSave={saveMailbox}
          onClose={() => setMbxForm(null)}
        />
      )}
      {ruleForm && (
        <RuleModal
          form={ruleForm}
          setForm={setRuleForm}
          onSave={saveRule}
          onClose={() => setRuleForm(null)}
          onDryRun={(rid) => runDryRun({ rule_id: rid, name: ruleForm?.name })}
          folders={folders}
          foldersError={foldersError}
        />
      )}
      {historyOpen && (
        <HistoryModal
          items={history}
          loading={historyLoading}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {dryRun && (
        <DryRunModal
          data={dryRun}
          onClose={() => setDryRun(null)}
        />
      )}
    </div>
  );
}

/* ============================================================
   Modals
   ============================================================ */

function MailboxModal({ form, setForm, onSave, onClose }) {
  const isEdit = !!form.editing_id;
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" data-testid="mailbox-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">{isEdit ? "Edytuj skrzynkę" : "Nowa skrzynka IMAP"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Nazwa">
            <input className="input-field" value={form.name} onChange={(e) => upd({ name: e.target.value })} data-testid="mailbox-field-name" />
          </Field>
          <Field label="Adres email">
            <input className="input-field" type="email" value={form.email} onChange={(e) => upd({ email: e.target.value })} data-testid="mailbox-field-email" />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Field label="Serwer IMAP">
                <input className="input-field" value={form.imap_host} onChange={(e) => upd({ imap_host: e.target.value })} data-testid="mailbox-field-host" />
              </Field>
            </div>
            <Field label="Port">
              <input className="input-field" type="number" value={form.imap_port} onChange={(e) => upd({ imap_port: parseInt(e.target.value || 0, 10) })} data-testid="mailbox-field-port" />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.imap_ssl} onChange={(e) => upd({ imap_ssl: e.target.checked })} data-testid="mailbox-field-ssl" />
            Użyj SSL / TLS
          </label>
          <Field label="Login (zwykle email)">
            <input className="input-field" value={form.username} onChange={(e) => upd({ username: e.target.value })} data-testid="mailbox-field-username" />
          </Field>
          <Field label={isEdit ? "Nowe hasło (puste = bez zmian)" : "Hasło / App Password"}>
            <input className="input-field" type="password" value={form.password} onChange={(e) => upd({ password: e.target.value })} data-testid="mailbox-field-password" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Auto-sync co (min, 0 = ręcznie)">
              <input className="input-field" type="number" min={0} max={1440} value={form.sync_interval_minutes} onChange={(e) => upd({ sync_interval_minutes: parseInt(e.target.value || 0, 10) })} data-testid="mailbox-field-interval" />
            </Field>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm py-2">
                <input type="checkbox" checked={!!form.active} onChange={(e) => upd({ active: e.target.checked })} data-testid="mailbox-field-active" />
                Aktywna
              </label>
            </div>
          </div>
          <div className="border border-slate-200 rounded p-3 bg-slate-50/50">
            <div className="text-xs font-semibold text-slate-600 mb-2">Które wiadomości sprawdzać przy synchronizacji</div>
            <label className="flex items-start gap-2 text-sm mb-1.5">
              <input type="radio" name="search_mode" checked={form.search_mode === "unseen"} onChange={() => upd({ search_mode: "unseen" })} data-testid="mailbox-field-mode-unseen" className="mt-1" />
              <div>
                <div>Tylko nieprzeczytane <span className="text-slate-400">(domyślnie, szybciej)</span></div>
                <div className="text-[11px] text-slate-500">Wiadomości otwarte wcześniej w innej aplikacji nie zostaną posortowane.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="search_mode" checked={form.search_mode === "recent"} onChange={() => upd({ search_mode: "recent" })} data-testid="mailbox-field-mode-recent" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  Ostatnie
                  <input
                    className="input-field !w-20 !py-0.5"
                    type="number"
                    min={1}
                    max={200}
                    value={form.search_limit}
                    onChange={(e) => upd({ search_limit: parseInt(e.target.value || 20, 10) })}
                    disabled={form.search_mode !== "recent"}
                    data-testid="mailbox-field-search-limit"
                  />
                  wiadomości
                </div>
                <div className="text-[11px] text-slate-500">Ponownie sprawdza również wiadomości już przeczytane — użyj, jeśli sortujesz maile także z webmaila.</div>
              </div>
            </label>
          </div>
          {!isEdit && (
            <div className="text-xs text-slate-500 bg-slate-50 rounded p-2 border border-slate-200">
              Titan Email domyślnie: <span className="font-mono">imap.titan.email</span>, port <span className="font-mono">993</span>, SSL włączone.
              Hasło (App Password) generujesz w panelu poczty → Ustawienia → Bezpieczeństwo.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm">Anuluj</button>
          <button onClick={onSave} className="bg-[#2563EB] hover:bg-[#1E40AF] text-white px-4 py-2 rounded-md text-sm" data-testid="mailbox-save-btn">Zapisz</button>
        </div>
      </div>
    </div>
  );
}

function RuleModal({ form, setForm, onSave, onClose, onDryRun, folders = [], foldersError = null }) {
  const isEdit = !!form.editing_id;
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const updateCond = (idx, patch) => setForm((f) => ({
    ...f,
    conditions: f.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
  }));
  const addCond = () => setForm((f) => ({ ...f, conditions: [...f.conditions, { field: "subject", op: "contains", value: "" }] }));
  const rmCond = (idx) => setForm((f) => ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) }));

  const updateAct = (idx, patch) => setForm((f) => ({
    ...f,
    actions: f.actions.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
  }));
  const addAct = () => setForm((f) => ({ ...f, actions: [...f.actions, { type: "mark_read", folder: null }] }));
  const rmAct = (idx) => setForm((f) => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) }));

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="rule-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">{isEdit ? "Edytuj regułę" : "Nowa reguła sortowania"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Field label="Nazwa reguły">
                <input className="input-field" value={form.name} onChange={(e) => upd({ name: e.target.value })} data-testid="rule-field-name" />
              </Field>
            </div>
            <Field label="Priorytet (mniej = wyżej)">
              <input className="input-field" type="number" value={form.priority} onChange={(e) => upd({ priority: parseInt(e.target.value || 100, 10) })} data-testid="rule-field-priority" />
            </Field>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="match" checked={form.match === "all"} onChange={() => upd({ match: "all" })} data-testid="rule-field-match-all" />
              Wszystkie warunki (AND)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="match" checked={form.match === "any"} onChange={() => upd({ match: "any" })} data-testid="rule-field-match-any" />
              Dowolny warunek (OR)
            </label>
            <label className="flex items-center gap-2 text-sm ml-auto">
              <input type="checkbox" checked={!!form.active} onChange={(e) => upd({ active: e.target.checked })} data-testid="rule-field-active" />
              Aktywna
            </label>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Warunki</div>
            <div className="space-y-2">
              {form.conditions.map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`rule-condition-${i}`}>
                  <select className="input-field flex-1" value={c.field} onChange={(e) => updateCond(i, { field: e.target.value, op: e.target.value === "has_attachment" ? "is_true" : "contains" })}>
                    {Object.entries(FIELD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <select className="input-field" value={c.op} onChange={(e) => updateCond(i, { op: e.target.value })}>
                    {c.field === "has_attachment" ? (
                      <>
                        <option value="is_true">{OP_LABELS.is_true}</option>
                        <option value="is_false">{OP_LABELS.is_false}</option>
                      </>
                    ) : (
                      <>
                        <option value="contains">{OP_LABELS.contains}</option>
                        <option value="equals">{OP_LABELS.equals}</option>
                        <option value="starts_with">{OP_LABELS.starts_with}</option>
                        <option value="ends_with">{OP_LABELS.ends_with}</option>
                        <option value="regex">{OP_LABELS.regex}</option>
                      </>
                    )}
                  </select>
                  {c.field !== "has_attachment" && (
                    <input className="input-field flex-1" placeholder="wartość" value={c.value || ""} onChange={(e) => updateCond(i, { value: e.target.value })} />
                  )}
                  <button onClick={() => rmCond(i)} className="p-1 hover:bg-red-100 rounded"><Trash className="w-4 h-4 text-red-600" /></button>
                </div>
              ))}
              <button onClick={addCond} className="text-xs text-blue-700 hover:underline flex items-center gap-1" data-testid="rule-add-condition">
                <Plus className="w-3 h-3" /> Dodaj warunek
              </button>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Akcje</div>
            <div className="space-y-2">
              {form.actions.map((a, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`rule-action-${i}`}>
                  <select className="input-field flex-1" value={a.type} onChange={(e) => updateAct(i, { type: e.target.value, folder: (e.target.value === "move" || e.target.value === "copy") ? (a.folder || "") : null })}>
                    {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  {(a.type === "move" || a.type === "copy") && (
                    <input
                      className="input-field flex-1"
                      list="imap-folders-list"
                      placeholder="np. INBOX.Faktury"
                      value={a.folder || ""}
                      onChange={(e) => updateAct(i, { folder: e.target.value })}
                      data-testid={`rule-action-${i}-folder`}
                    />
                  )}
                  <button onClick={() => rmAct(i)} className="p-1 hover:bg-red-100 rounded"><Trash className="w-4 h-4 text-red-600" /></button>
                </div>
              ))}
              <datalist id="imap-folders-list">
                {folders.map((f) => (<option key={f} value={f} />))}
              </datalist>
              {folders.length > 0 && (
                <div className="text-[11px] text-slate-500">
                  Podpowiedzi z serwera: {folders.slice(0, 5).join(", ")}{folders.length > 5 ? ` … (${folders.length})` : ""}
                </div>
              )}
              {foldersError && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Nie udało się pobrać listy folderów ze skrzynki. Wpisz nazwę ręcznie (np. <span className="font-mono">INBOX.Faktury</span>).
                </div>
              )}
              <button onClick={addAct} className="text-xs text-blue-700 hover:underline flex items-center gap-1" data-testid="rule-add-action">
                <Plus className="w-3 h-3" /> Dodaj akcję
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          {isEdit && onDryRun && (
            <button
              onClick={() => onDryRun(form.editing_id)}
              className="border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-1 mr-auto"
              title="Sprawdź, które z ostatnich 50 wiadomości pasują do tej reguły"
              data-testid="rule-dryrun-btn"
            >
              <Play className="w-4 h-4" /> Testuj tę regułę
            </button>
          )}
          <button onClick={onClose} className="border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm">Anuluj</button>
          <button onClick={onSave} className="bg-[#2563EB] hover:bg-[#1E40AF] text-white px-4 py-2 rounded-md text-sm" data-testid="rule-save-btn">Zapisz</button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ items, loading, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md w-full max-w-3xl p-6 max-h-[85vh] overflow-y-auto" data-testid="history-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2">
            <ClockCounterClockwise className="w-5 h-5" /> Historia synchronizacji
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
            <CircleNotch className="w-4 h-4 animate-spin" /> Ładowanie…
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">Brak wpisów w historii.</div>
        ) : (
          <div className="space-y-2">
            {items.map((h) => (
              <details key={h.id} className="border border-slate-200 rounded" data-testid={`history-item-${h.id}`}>
                <summary className="px-3 py-2 cursor-pointer hover:bg-slate-50 flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${h.ok ? "bg-green-500" : "bg-red-500"}`} />
                  <span className="font-mono text-xs">{h.started_at?.slice(0, 19).replace("T", " ")}</span>
                  <span className="text-slate-700">
                    proc. <b>{h.processed}</b> · dopasowań <b>{h.matched}</b> · akcji <b>{h.actions_applied}</b>
                  </span>
                  <span className="text-[11px] text-slate-500 ml-auto">
                    {h.duration_ms != null ? `${h.duration_ms} ms` : ""}
                  </span>
                </summary>
                <div className="p-3 border-t border-slate-100 text-xs">
                  {h.error && (
                    <div className="mb-2 text-red-700 bg-red-50 border border-red-200 rounded p-2">
                      Błąd: <span className="font-mono">{h.error}</span>
                    </div>
                  )}
                  {(h.per_message || []).length === 0 ? (
                    <div className="text-slate-500">Brak wiadomości do przetworzenia.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {(h.per_message || []).map((pm, i) => (
                        <li key={i} className="py-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 font-mono w-14 shrink-0">UID {pm.uid}</span>
                            <span className="font-semibold truncate">{pm.from || "(brak)"}</span>
                            {pm.matched_rule && (
                              <span className="text-[10px] bg-green-100 text-green-800 rounded px-1.5 py-0.5">→ {pm.matched_rule}</span>
                            )}
                          </div>
                          <div className="text-slate-600 truncate pl-16">{pm.subject || "(bez tematu)"}</div>
                          {pm.actions?.length ? (
                            <div className="pl-16 mt-0.5 flex flex-wrap gap-1">
                              {pm.actions.map((a, j) => <ActionChip key={j} action={a} />)}
                            </div>
                          ) : null}
                          {pm.error && <div className="pl-16 text-red-700">{pm.error}</div>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DryRunModal({ data, onClose }) {
  const res = data.result || {};
  const items = res.per_message || [];
  const matchedItems = items.filter((x) => x.matched_rule);
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md w-full max-w-3xl p-6 max-h-[85vh] overflow-y-auto" data-testid="dryrun-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2">
            <Play className="w-5 h-5" />
            Dry-run {data.name ? <span className="text-slate-500 text-sm font-normal">— reguła „{data.name}"</span> : null}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        {!res.ok ? (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
            Błąd: {res.error || "nieznany"}
          </div>
        ) : (
          <>
            <div className="text-sm mb-3 flex gap-4">
              <span>Sprawdzono: <b>{res.processed}</b></span>
              <span>Pasowałoby: <b className="text-green-700">{res.matched}</b></span>
              <span className="text-slate-500">Akcje nie zostały wykonane (dry-run).</span>
            </div>
            {items.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">Skrzynka jest pusta.</div>
            ) : (
              <ul className="divide-y divide-slate-100 text-xs" data-testid="dryrun-list">
                {items.map((pm, i) => {
                  const hit = !!pm.matched_rule;
                  return (
                    <li key={i} className={`py-1.5 ${hit ? "bg-green-50" : ""}`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${hit ? "bg-green-500" : "bg-slate-300"}`} />
                        <span className="text-slate-400 font-mono w-14 shrink-0">UID {pm.uid}</span>
                        <span className="font-semibold truncate">{pm.from || "(brak)"}</span>
                        {hit && <span className="text-[10px] bg-green-100 text-green-800 rounded px-1.5 py-0.5">→ {pm.matched_rule}</span>}
                      </div>
                      <div className="text-slate-600 truncate pl-16">{pm.subject || "(bez tematu)"}</div>
                      {hit && pm.actions?.length ? (
                        <div className="pl-16 mt-0.5 flex flex-wrap gap-1">
                          {pm.actions.map((a, j) => <ActionChip key={j} action={a} />)}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {matchedItems.length === 0 && items.length > 0 && (
              <div className="mt-3 text-xs text-slate-500 italic">Żadna z ostatnich {items.length} wiadomości nie pasuje.</div>
            )}
          </>
        )}
        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="bg-[#2563EB] hover:bg-[#1E40AF] text-white px-4 py-2 rounded-md text-sm">Zamknij</button>
        </div>
      </div>
    </div>
  );
}

function ActionChip({ action }) {
  // Backwards-compatible: legacy history entries stored actions as strings
  // like "move:Faktury". New entries are objects with {type, folder, ok, error, dry_run?}.
  if (typeof action === "string") {
    return <span className="inline-block bg-slate-100 rounded px-1.5 py-0.5 text-[10px]">{action}</span>;
  }
  const label = (ACTION_LABELS[action.type] || action.type) + (action.folder ? ` → ${action.folder}` : "");
  if (action.dry_run) {
    return <span className="inline-block bg-blue-50 text-blue-800 border border-blue-200 rounded px-1.5 py-0.5 text-[10px]">{label} <span className="text-blue-500">(preview)</span></span>;
  }
  if (action.ok === false) {
    return (
      <span
        className="inline-block bg-red-50 text-red-800 border border-red-200 rounded px-1.5 py-0.5 text-[10px]"
        title={action.error || "błąd"}
      >
        ✗ {label}{action.error ? ` — ${action.error}` : ""}
      </span>
    );
  }
  return <span className="inline-block bg-green-50 text-green-800 border border-green-200 rounded px-1.5 py-0.5 text-[10px]">✓ {label}</span>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
